/**
 * Extract CLP/GHS hazard-statement codes (H and EUH) from free text — e.g. the
 * text pulled out of an SDS PDF.
 *
 *   - H codes:   H followed by 3 digits in the 2xx / 3xx / 4xx range (H319, H336…)
 *   - EUH codes: EUH followed by 3 digits (EUH066, EUH208…)
 *
 * Combined statements written as "H315 + H319" naturally yield both individual
 * codes (the "+" is a boundary). Results are upper-cased and de-duplicated in
 * document order. Extraction is deliberately permissive — codes that don't exist
 * in the catalog simply fail to resolve later and are dropped.
 */
const H_CODE_TOKEN = /(^|[^A-Za-z0-9])((?:EUH\d{3})|(?:H[2-4]\d{2}))(?![A-Za-z0-9])/gi;

/**
 * Every distinct H/EUH hazard-statement code found anywhere in the SDS text —
 * the product's own classification plus any codes listed in the per-ingredient
 * breakdown (Section 3) and the glossary of referenced statements (Section 16).
 * We deliberately collect them all: the register needs the complete set of
 * hazard statements associated with the SDS, not just the headline ones.
 * Results are upper-cased and de-duplicated in document order.
 */
export function extractHStatementCodes(text: string): string[] {
  if (!text) {
    return [];
  }

  const seen = new Set<string>();
  H_CODE_TOKEN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = H_CODE_TOKEN.exec(text)) !== null) {
    const code = match[2].toUpperCase();
    seen.add(code);
  }
  return Array.from(seen);
}
